import { type FC, useState, useMemo, useContext, useEffect } from "react";
import { NotAuthorized } from "@/components/NotAuthorized";
import { LoadingState } from "@/components/podkit/loading/LoadingState";
import { useDocumentTitle } from "@/hooks/use-document-title";
import { useMembership } from "@/hooks/use-membership";
import { useOrganization } from "@/queries/organization-queries";
import { useEnterpriseCreditUsageReport } from "@/queries/enterprise-usage-queries";
import { OrganizationRole } from "gitpod-next-api/gitpod/v1/organization_pb";
import { Text } from "@/components/podkit/typography/Text";
import { Code, ConnectError } from "@connectrpc/connect";
import { Card } from "@/components/podkit/Card";
import { Pill } from "@/components/podkit/status/Pill";
import { Select, SelectContent, SelectItem, SelectTrigger } from "@/components/podkit/select/Select";
import { EnterpriseUsageDailyChart } from "@/routes/settings/organization/enterprise-usage/EnterpriseUsageDailyChart";
import { SettingsPageContext } from "@/contexts/SettingsPageContext";
import {
    buildMonthRanges,
    buildDailyChartData,
    computeTopUsers,
    computeTotalCredits,
    formatCredits,
    formatUpdatedAt,
    type Grouping,
    type UsageTypeFilter,
} from "@/routes/settings/organization/enterprise-usage/enterprise-usage-helpers";

const EnterpriseUsagePage: FC = () => {
    useDocumentTitle("Usage");

    const { setBreadCrumbRowAction } = useContext(SettingsPageContext);

    // Computed once on mount — intentionally not reactive to date changes.
    // If the tab stays open past a month boundary the list won't update until remount.
    const monthRanges = useMemo(() => buildMonthRanges(), []);
    const [selectedMonth, setSelectedMonth] = useState(monthRanges[0]?.value ?? "");
    const [selectedType, setSelectedType] = useState<UsageTypeFilter>("all");
    const [grouping, setGrouping] = useState<Grouping>("users");

    const { data: organization, isLoading: isLoadingOrganization } = useOrganization();
    const { membership, isPending: isLoadingMembership } = useMembership();

    const organizationId = organization?.id ?? "";
    const activeRange = useMemo(
        () => monthRanges.find((r) => r.value === selectedMonth) ?? monthRanges[0],
        [monthRanges, selectedMonth],
    );

    const {
        data: reportData,
        isLoading: isLoadingReport,
        error,
    } = useEnterpriseCreditUsageReport(organizationId, activeRange.start, activeRange.end);

    const updatedAt = formatUpdatedAt(reportData?.updatedAt);

    useEffect(() => {
        setBreadCrumbRowAction(
            <HeaderControls
                monthRanges={monthRanges}
                selectedMonth={selectedMonth}
                onMonthChange={setSelectedMonth}
                selectedType={selectedType}
                onTypeChange={setSelectedType}
                updatedAt={updatedAt}
            />,
        );

        return () => {
            setBreadCrumbRowAction(null);
        };
    }, [setBreadCrumbRowAction, monthRanges, selectedMonth, selectedType, updatedAt]);

    if (isLoadingMembership || isLoadingOrganization) {
        return <LoadingState />;
    }

    if (membership?.userRole !== OrganizationRole.ADMIN) {
        return <NotAuthorized />;
    }

    if (error instanceof ConnectError && error.code === Code.PermissionDenied) {
        return <NotAuthorized />;
    }

    const errorMessage = deriveErrorMessage(error);
    const dailyUsage = reportData?.dailyUsage ?? [];

    return (
        <EnterpriseUsageContent
            dailyUsage={dailyUsage}
            isLoading={isLoadingReport}
            selectedMonthLabel={activeRange.label}
            selectedType={selectedType}
            grouping={grouping}
            onGroupingChange={setGrouping}
            errorMessage={errorMessage}
        />
    );
};

export default EnterpriseUsagePage;

// ─── Sub-components ─────────────────────────────────────────────────────────

import type { PlainDailyCreditUsage } from "@/queries/enterprise-usage-queries";
import type { MonthRange } from "@/routes/settings/organization/enterprise-usage/enterprise-usage-helpers";

function deriveErrorMessage(error: Error | null): string | undefined {
    if (!error) return undefined;
    if (error instanceof ConnectError && (error.code === Code.NotFound || error.code === Code.FailedPrecondition)) {
        return "Usage data is not available for this organization.";
    }
    return "We\u2019re having trouble loading usage data. Try selecting a different month or refreshing the page.";
}

type HeaderControlsProps = {
    monthRanges: MonthRange[];
    selectedMonth: string;
    onMonthChange: (v: string) => void;
    selectedType: UsageTypeFilter;
    onTypeChange: (v: UsageTypeFilter) => void;
    updatedAt: string;
};

const HeaderControls: FC<HeaderControlsProps> = ({
    monthRanges,
    selectedMonth,
    onMonthChange,
    selectedType,
    onTypeChange,
    updatedAt,
}) => (
    <div className="flex items-center gap-3">
        {updatedAt && (
            <Text className="hidden text-xs text-content-tertiary lg:block">
                Updated daily · Last: {updatedAt} (UTC)
            </Text>
        )}

        <Select value={selectedMonth} onValueChange={onMonthChange}>
            <SelectTrigger
                loading={false}
                className="inline-flex w-auto min-w-0 gap-1 rounded-md border-border-light px-3 py-1.5 text-sm"
            >
                <span>{monthRanges.find((r) => r.value === selectedMonth)?.label ?? selectedMonth}</span>
            </SelectTrigger>
            <SelectContent>
                {monthRanges.map((r) => (
                    <SelectItem key={r.value} value={r.value}>
                        <span>{r.label}</span>
                    </SelectItem>
                ))}
            </SelectContent>
        </Select>

        <Select value={selectedType} onValueChange={(v) => onTypeChange(v as UsageTypeFilter)}>
            <SelectTrigger
                loading={false}
                className="inline-flex w-auto min-w-0 gap-1 rounded-md border-border-light px-3 py-1.5 text-sm"
            >
                <span>
                    {selectedType === "all" ? "All Types" : selectedType === "environment" ? "Environment" : "Intelligence"}
                </span>
            </SelectTrigger>
            <SelectContent>
                <SelectItem value="all">
                    <span>All Types</span>
                </SelectItem>
                <SelectItem value="environment">
                    <span>Environment</span>
                </SelectItem>
                <SelectItem value="agentic">
                    <span>Intelligence</span>
                </SelectItem>
            </SelectContent>
        </Select>
    </div>
);

type ContentProps = {
    dailyUsage: PlainDailyCreditUsage[];
    isLoading: boolean;
    selectedMonthLabel: string;
    selectedType: UsageTypeFilter;
    grouping: Grouping;
    onGroupingChange: (g: Grouping) => void;
    errorMessage?: string;
};

const EnterpriseUsageContent: FC<ContentProps> = ({
    dailyUsage,
    isLoading,
    selectedMonthLabel,
    selectedType,
    grouping,
    onGroupingChange,
    errorMessage,
}) => {
    const topUsers = useMemo(() => computeTopUsers(dailyUsage, 10), [dailyUsage]);
    const chartData = useMemo(
        () => buildDailyChartData(dailyUsage, grouping, topUsers, selectedType),
        [dailyUsage, grouping, topUsers, selectedType],
    );
    const totalCredits = useMemo(
        () => computeTotalCredits(dailyUsage, selectedType),
        [dailyUsage, selectedType],
    );

    return (
        <div className="flex max-w-[900px] flex-col gap-4 pt-2" data-testid="enterprise-usage-page">
            <SummaryCard totalCredits={totalCredits} monthLabel={selectedMonthLabel} />

            <EnterpriseUsageDailyChart
                chartData={chartData}
                grouping={grouping}
                onGroupingChange={onGroupingChange}
                topUsers={topUsers}
                loading={isLoading}
                monthLabel={selectedMonthLabel}
                errorMessage={errorMessage}
            />
        </div>
    );
};

const SummaryCard: FC<{ totalCredits: number; monthLabel: string }> = ({ totalCredits, monthLabel }) => (
    <Card className="p-5">
        <div className="flex items-center gap-2">
            <Text className="text-sm text-content-secondary">Monthly total</Text>
            <Pill variant="neutral" size="sm">
                {monthLabel}
            </Pill>
        </div>
        <div className="mt-1 flex items-baseline gap-1.5">
            {totalCredits > 0 ? (
                <>
                    <span className="text-2xl font-bold text-content-primary">{formatCredits(totalCredits)}</span>
                    <span className="text-sm text-content-tertiary">credits</span>
                </>
            ) : (
                <span className="text-2xl text-content-tertiary">&mdash;</span>
            )}
        </div>
    </Card>
);
